import React, { useState, useEffect } from "react";
import { Modal, Table, Button, Spin, Empty } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { getApi } from "redux/apis";
import styles from "./index.module.scss";
 
const { Column } = Table;
 
export interface UseDateLogRecord {
  useDate: string | null;
  remainingItems: number;
  timestamp: string;
}
 
interface Props {
  visible: boolean;
  onClose: () => void;
  itemCode: string;
  serviceCenterId: string;
  data: UseDateLogRecord[];
  loading?: boolean;
}
 
// Dummy data generator for testing infinite scroll

 
const UseDateLogModal: React.FC<Props> = ({
  visible,
  onClose,
  itemCode,
  serviceCenterId,
  data: initialData,
  loading: initialLoading = false,
}) => {
  const [logData, setLogData] = useState<UseDateLogRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 10; // Increased limit for better testing
 
  // Reset state when modal opens/closes or itemCode changes
  useEffect(() => {
    if (visible && itemCode && serviceCenterId) {
      setLogData([]);
      setCurrentPage(1);
      setHasMore(true);
      setTotalRecords(0);
      // Use initial data if provided, otherwise fetch fresh data
      if (initialData && initialData.length > 0) {
        setLogData(initialData);
        setHasMore(initialData.length >= limit);
      } else {
        fetchInventoryLogs(1, true);
      }
    }
  }, [visible, itemCode, serviceCenterId]);
 
  // Use initial loading state
  useEffect(() => {
    setLoading(initialLoading);
  }, [initialLoading]);
 
  const fetchInventoryLogs = async (page: number, isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
 
      try {
        // Try to fetch from API first
        const response = await getApi(
          `/inventory/logs/${itemCode}/${serviceCenterId}?page=${page}&limit=${limit}`
        );
 
        if (response.data.success) {
          const newData = response.data.data;
          const pagination = response.data.pagination;
 
          if (isInitialLoad) {
            setLogData(newData);
          } else {
            setLogData(prev => [...prev, ...newData]);
          }
 
          setHasMore(pagination.hasMore);
          setTotalRecords(pagination.totalRecords);
          setCurrentPage(pagination.currentPage);
        } else {
          throw new Error('API returned unsuccessful response');
        }
      } catch (apiError) {
        console.warn('API failed, using dummy data for testing:', apiError);
 
        // Fallback to dummy data for testing infinite scroll
      
        const maxRecords = 50; // Simulate total of 50 records for testing
 
       
 
        setHasMore(page * limit < maxRecords);
        setTotalRecords(maxRecords);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('Error fetching inventory logs:', error);
      if (isInitialLoad) {
        setLogData([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Infinite scroll handler
  const handleScroll = (e: any) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    // Check if scrolled to bottom (with small threshold)
    if (
      scrollHeight - scrollTop - clientHeight < 1 &&
      !loadingMore &&
      hasMore
    ) {
      fetchInventoryLogs(currentPage + 1);
    }
  };
 
  const handleClose = () => {
    setLogData([]);
    setCurrentPage(1);
    setHasMore(true);
    setTotalRecords(0);
    onClose();
  };
 
  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose} className="cancel-button">
          Cancel
        </Button>
      ]}
      width={800}
      centered
      destroyOnClose
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <ClockCircleOutlined />
          Use date log: {itemCode}
        </div>
      }
    >
      <div>
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <Spin size="large" />
            <div>Loading inventory logs...</div>
          </div>
        ) : (
          <>
            <Table
              dataSource={logData}
              rowKey={(record, i) => `${record.timestamp}-${i}`}
              pagination={false}
              className={styles["use-date-log-modal__table"]}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No logs found"
                  />
                )
              }}
              scroll={{ y: 400 }}
              onScroll={handleScroll}
            >
              <Column
                title="Use Date"
                dataIndex="timestamp"
                key="timestamp"
                render={(d: string | null) =>
                  d ? moment(d).format("MM/DD/YYYY") : "-"
                }
              />
              <Column
                title="Remaining Items"
                dataIndex="newQty"
                key="newQty"
              />
              <Column
                title="Timestamp"
                dataIndex="timestamp"
                key="timestamp"
                render={(ts: string) =>
                  moment(ts).format("MM/DD/YYYY hh:mm A")
                }
              />
            </Table>
 
            {/* Loading indicator for infinite scroll */}
            {loadingMore && (
              <div style={{
                textAlign: "center",
                marginTop: "16px",
                padding: "16px"
              }}>
                <Spin size="small" />
                <span style={{ marginLeft: "8px" }}>Loading more...</span>
              </div>
            )}
 
            {/* Records count info */}
            {totalRecords > 0 && (
              <div style={{
                textAlign: "center",
                marginTop: "8px",
                color: "#666",
                fontSize: "12px"
              }}>
                Showing {logData.length} of {totalRecords} records
                {!hasMore && " (All records loaded)"}
              </div>
            )}
 
            {/* End of records indicator */}
            {/* {!hasMore && logData.length > 0 && (
              <div style={{
                textAlign: "center",
                marginTop: "16px",
                padding: "16px",
                color: "#999",
                fontSize: "12px",
                borderTop: "1px solid #f0f0f0"
              }}>
                — End of records —
              </div>
            )} */}
          </>
        )}
      </div>
    </Modal>
  );
};
 
export default UseDateLogModal;

